import cn from 'classnames';
import type React from 'react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
} from 'react';
import { Tooltip } from '@mui/material';
import { CloseForSearch, Search as SearchIcon } from '@shared/assets';
import { IconButton, Tab } from '@shared/ui';
import * as st from './styles.module.scss';

type Props = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  search: string | undefined;
  onSearch: (search: string) => void;
  onToggle?: () => void;
  mobile?: boolean;
  titles?: string[];
  onClose?: () => void;
  shouldClose?: boolean;
  isLoading?: boolean;
  isOpenInit?: boolean;
  whereUse?: 'nav' | 'notes';
  additionalChildren?: React.ReactNode;
};

export const Search: React.FC<Props> = ({
  search,
  onSearch,
  onToggle,
  mobile = false,
  titles,
  onClose,
  shouldClose,
  isLoading,
  isOpenInit = false,
  whereUse = 'nav',
  additionalChildren,
  ...props
}) => {
  const [hideBackground, sethideBackground] = useState<boolean>(isOpenInit ? false : mobile);

  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const onOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      sethideBackground(true);
      onToggle?.();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(e.target.value);
    sethideBackground(true);
  };

  const pressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (search) onSearch(search);
      sethideBackground(false);
    }
  };

  const handleLiButton = (title: string) => {
    onSearch(title);
    if (hideBackground) sethideBackground(false);
  };

  const handleCancel = () => {
    if (mobile && inputRef.current) {
      inputRef.current.focus();
      sethideBackground(true);
    }

    if (search) onSearch('');

    if (!mobile) {
      sethideBackground(false);
      setIsOpen(false);
    }
    onToggle?.();
  };

  useEffect(() => {
    shouldClose && handleCancel();
  }, [shouldClose]);

  useLayoutEffect(() => {
    isOpenInit && !isOpen && setIsOpen(true);
  }, [isOpenInit, isOpen, mobile, hideBackground]);

  return (
    <div className={cn(st.wrapper, (isOpen || mobile) && st.open)}>
      {additionalChildren && (
        <section className={cn(st.additionalChild, isOpen && st.open)}>
          {additionalChildren}
        </section>
      )}

      <div className={cn(st.search, (isOpen || mobile) && st.open)} onClick={onOpen}>
        {/* <Tooltip title={!isOpen && 'Поиск'} disableInteractive> */}
        {(whereUse === 'nav' || isOpen || mobile) && (
          <>
            <Tooltip title="Поиск">
              <div className={st.searchIcon}>
                <SearchIcon />
              </div>
            </Tooltip>
          </>
        )}

        {whereUse === 'notes' && !isOpen && !mobile && (
          <Tooltip title="Поиск">
            <div>
              <Tab isActive needHover>
                {/* <div className={st.icon}> */}
                <SearchIcon />
              </Tab>
            </div>
          </Tooltip>
        )}
        {/* </Tooltip> */}

        {(isOpen || mobile) && (
          <input
            autoFocus
            ref={inputRef}
            className={cn(st.input, (mobile || isOpen) && st.open)}
            type="search"
            name="search"
            placeholder="Поиск..."
            value={search}
            spellCheck={false}
            autoComplete="off"
            onChange={handleChange}
            onKeyUp={pressEnter}
            {...props}
          />
        )}

        <div className={cn(st.closeIcon, (mobile || isOpen) && st.open)}>
          <IconButton
            variant={`${!mobile ? 'dark' : 'transparent'}`}
            className={st.close}
            onClick={() => {
              handleCancel();
              onClose && onClose();
            }}
            size="medium-large"
          >
            <CloseForSearch />
          </IconButton>
        </div>

        {(mobile || isOpen) && search && hideBackground && whereUse === 'nav' && (
          <>
            <ul className={st.listGroup}>
              {isLoading ? (
                <div className={st.liNotFound}>
                  <div className={st.buttonNotFound}>Поиск...</div>
                </div>
              ) : null}

              {!isLoading && titles && titles.length === 0 && (
                <div className={st.liNotFound}>
                  <div className={st.buttonNotFound}>Ничего не найдено</div>
                </div>
              )}

              {titles &&
                titles.length !== 0 &&
                hideBackground &&
                titles.map((title, index) => {
                  if (title.toLowerCase().indexOf(search.toLowerCase()) !== -1) {
                    return (
                      <li className={st.li} key={index}>
                        {/*<SearchIcon />*/}
                        <button
                          type="submit"
                          onClick={(e) => {
                            e.currentTarget.value = title;
                            handleLiButton(title);
                          }}
                          className={st.button}
                        >
                          {title
                            .split(new RegExp(`(${search})`, 'ig'))
                            .map((part, partIndex) =>
                              part.toLowerCase() === search.toLowerCase() ? (
                                <strong key={partIndex}>{part}</strong>
                              ) : (
                                part
                              )
                            )}
                        </button>
                      </li>
                    );
                  }
                })}
            </ul>
          </>
        )}
        {whereUse === 'nav' && hideBackground && <div className={st.backList}></div>}
      </div>
    </div>
  );
};
